import React, { useState } from 'react';
import { Form, Modal, Button } from 'react-bootstrap';
import { useFormik } from 'formik';
import { useAppDispatch, useAppSelector } from '../../../hooks/defaultHooks';
import { closeModal } from '../../../store/slices/modalSlice';
import { modalExtra } from '../../../selectors/selectors';
import { isFetchBaseQueryError } from '../../../utils/helpers';
import { useAdjustConsumerTokensMutation } from '../../consumer/consumer.service';
import { TokenDirection } from '../../consumer/consumer.types';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import cls from './AdjustConsumerTokens.module.scss';

const AdjustConsumerTokens = () => {
  const extra = useAppSelector(modalExtra);
  const dispatch = useAppDispatch();
  const [adjustTokens] = useAdjustConsumerTokensMutation();
  const [disabled, setDisabled] = useState<boolean>(false);
  const resetHandler = extra?.handler as () => void;
  const closeHandler = (): void => {
    dispatch(closeModal());
  };
  const validationSchema = yup.object().shape({
    amount: yup
      .number()
      .integer('Tokens must be a whole number')
      .min(1, 'Minimum field value 1')
      .required('This field is required'),
    comment: yup.string().trim().required('This field is required'),
  });

  const formik = useFormik({
    initialValues: { amount: 0, direction: 'TOKEN_CREDIT' as TokenDirection, comment: '' },
    validationSchema,
    validateOnChange: true,
    onSubmit: async (values) => {
      const { amount, direction, comment } = values;
      setDisabled(true);
      try {
        const balance = await adjustTokens({
          id: extra?.id as string,
          direction,
          amount,
          comment: comment.trim(),
        }).unwrap();
        resetHandler();
        toast.success(
          `${balance.amount} tokens adjusted, purchased balance: ${balance.topupAvailable}`,
        );
        dispatch(closeModal());
      } catch (error) {
        if (isFetchBaseQueryError(error)) {
          if (error.status === 400) {
            toast.error('Please try again later');
          } else if (error.status === 403) {
            toast.error('Forbidden');
          } else if (error.status === 404) {
            toast.error('User not found');
          } else {
            toast.error('Oops. Something went wrong');
          }
        } else {
          toast.error('Oops. Something went wrong');
        }
      } finally {
        setDisabled(false);
      }
    },
  });
  const isSubmitDisabled = disabled || formik.values.amount < 1 || !formik.values.comment.trim();
  return (
    <>
      <Modal.Header className={cls.ModalHeader} closeButton>
        <h3 className="title-3" data-testid="modal-title">
          {extra?.title}
        </h3>
      </Modal.Header>
      <Modal.Body className={cls.ModalBody}>
        <div data-testid="text-part">
          <p>
            {extra?.supportText} <strong>{extra?.textInformation}</strong>
          </p>
        </div>
        <Form onSubmit={formik.handleSubmit} className={cls.Form}>
          <Form.Group className="position-relative">
            <Form.Label htmlFor="amount">Enter number of tokens</Form.Label>
            <Form.Control
              type="number"
              value={formik.values.amount}
              placeholder="Enter number of tokens"
              onChange={formik.handleChange}
              name="amount"
              id="amount"
              isInvalid={!!formik.errors.amount}
            />
            <Form.Control.Feedback type="invalid" tooltip>
              {formik.errors.amount}
            </Form.Control.Feedback>
          </Form.Group>
          <div>
            <label htmlFor="direction">Select operation type</label>
            <Form.Select
              onChange={(event) => formik.handleChange(event)}
              name="direction"
              id="direction"
            >
              <option value="TOKEN_CREDIT" id="TOKEN_CREDIT">
                Add tokens
              </option>
              <option value="TOKEN_DEBIT" id="TOKEN_DEBIT">
                Withdraw tokens
              </option>
            </Form.Select>
          </div>
          <Form.Group className="position-relative">
            <Form.Label htmlFor="comment">Reason</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formik.values.comment}
              placeholder="Why the balance is being changed"
              onChange={formik.handleChange}
              name="comment"
              id="comment"
              isInvalid={!!formik.errors.comment}
            />
            <Form.Control.Feedback type="invalid" tooltip>
              {formik.errors.comment}
            </Form.Control.Feedback>
          </Form.Group>
          <div className={cls.Buttons}>
            <Button type="submit" className={cls.Button} disabled={isSubmitDisabled}>
              {extra?.actionButtonText}
            </Button>
            <Button onClick={closeHandler} className={cls.CancelButton}>
              Cancel
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </>
  );
};

export default AdjustConsumerTokens;
